import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { FaChevronLeft } from 'react-icons/fa';

import PageTitle from 'modules/common/components/PageTitle';
import { IconButton, Text } from 'modules/common';
import { useNavigate } from 'modules/common/utils';

const headerTitle = (list) =>
  list?.map((item, i) => {
    const index = i;
    return (
      <div
        key={`title-${item}-${index}`}
        className={cn('inline space-x-3', {
          'last:text-primary-500': list?.length > 1,
        })}
      >
        <span>{item}</span>
        {i !== list?.length - 1 && <span className="font-black inline">-</span>}
      </div>
    );
  });

export const PageHeader = ({
  label,
  isLoading,
  tabPreTitle,
  withTitleContainer,
  containerClassName,
  withGoBack,
  goBack,
  trailingItem,
  trailingItemClassName,
}) => {
  const navigate = useNavigate();

  const goBackProps = React.useMemo(() => {
    if (typeof goBack === 'string') {
      return { to: goBack };
    }

    if (typeof goBack === 'function') {
      return {
        onClick: (e) => {
          e.preventDefault();
          goBack();
        },
      };
    }

    return { onClick: () => navigate() };
  }, [goBack, navigate]);

  const renderTitle = React.useMemo(() => {
    if (Array.isArray(label)) {
      const desktop = label?.flat()?.filter((x) => x && x !== '');
      const mobile = [label[0], label[label.length - 1]]?.filter((x) => x);

      return (
        <div className="flex">
          <div className="hidden md:block truncate last:truncate space-x-3">
            {headerTitle(desktop)}
          </div>
          <div className="block md:hidden truncate last:truncate space-x-3">
            {headerTitle(mobile)}
          </div>
        </div>
      );
    }
    return label;
  }, [label]);

  const renderTabTitle = React.useMemo(() => {
    if (typeof label === 'string') {
      return label;
    }

    if (Array.isArray(label) && label) {
      const $title = label?.flat()?.filter((x) => x && x !== '');
      if ($title.length > 1) {
        return $title.toString().replaceAll(',', ' - ');
      }
      return $title;
    }
    return '';
  }, [label]);

  return (
    <>
      <PageTitle
        title={`${tabPreTitle} ${
          label && !isLoading ? `| ${renderTabTitle}` : ''
        }`}
      />

      {(withTitleContainer || trailingItem) && (
        <div
          className={cn('flex items-center gap-ds-12', {
            'z-[22]': trailingItem,
          })}
        >
          {withTitleContainer && (
            <div className={cn('flex items-center flex-1', containerClassName)}>
              {(withGoBack || label) && (
                <div className="flex items-center gap-ds-16">
                  {withGoBack && (
                    <IconButton
                      icon={<FaChevronLeft className="h-3. w-3." />}
                      {...goBackProps}
                    />
                  )}

                  {label && (
                    <Text type="page-title" weight="bold">
                      {renderTitle}
                    </Text>
                  )}
                </div>
              )}
            </div>
          )}

          {trailingItem && (
            <div className="hidden items-center gap-ds-12 ml-auto md:flex">
              {trailingItem}
            </div>
          )}
        </div>
      )}

      {trailingItem && (
        <div
          className={cn(
            'flex md:hidden sticky top-0 !-mx-ds-16 !mt-0 border-b border-default z-[21]',
            trailingItemClassName
          )}
        >
          <div className="flex items-center gap-ds-12 w-full bg-[#F9FAFB] py-3.5 px-ds-16">
            {trailingItem}
          </div>
        </div>
      )}
    </>
  );
};

PageHeader.defaultProps = {
  tabPreTitle: process.env.REACT_APP_TITLE,
  label: '',
  isLoading: false,
  withTitleContainer: true,
  containerClassName: '',
  withGoBack: true,
  trailingItem: false,
  goBack: null,
  trailingItemClassName: '',
};

PageHeader.propTypes = {
  tabPreTitle: PropTypes.string,
  trailingItemClassName: PropTypes.string,
  label: PropTypes.string,
  isLoading: PropTypes.bool,
  withTitleContainer: PropTypes.bool,
  containerClassName: PropTypes.string,
  withGoBack: PropTypes.bool,
  goBack: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.instanceOf(Function),
  ]),
  trailingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
  ]),
};
function PageWrapper({ children, withScroll, className, containerClassName }) {
  const ref = React.useRef();

  return (
    <div className={cn('ds-page-outer-wrapper', containerClassName)}>
      <div className={cn('ds-page-inner-wrapper', className)} ref={ref}>
        <div
          className={cn('ds-page-wrapper md:p-1', {
            'md:overflow-hidden': !withScroll,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

PageWrapper.defaultProps = {
  children: <></>,
  className: false,
  withScroll: false,
  containerClassName: false,
};

PageWrapper.propTypes = {
  withScroll: PropTypes.bool,
  children: PropTypes.instanceOf(Object),
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  containerClassName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default PageWrapper;
